'use client';

import { useEffect } from 'react';

import { useSetAtom } from 'jotai';

import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';

import { saveCurrentCalculatorStateAtom } from '../../Atoms/PersistentCalculatorState.atom';
import type { FinanceHelpModalContent } from '../../Entities/FinanceHelpModal.entity';
import type { CalculatorMessages } from '../FinancingCalculator/Calculator.messages';
import { usePersistentCalculator } from '../FinancingCalculator/Hooks/UsePersistentCalculator';
import UniversalCalculator from '../FinancingCalculator/UniversalFinancingCalculator/UniversalCalculator';

type Props = {
  locale: Locale;
  vehicleId: string;
  vehicle: Vehicle;
  strings: CalculatorMessages;
  financeMessages: FinanceHelpModalContent;
  tenant?: Tenant | undefined;
};

/**
 * Client-side wrapper for the UniversalCalculator that ensures calculator state persistence
 * This automatically saves the calculator state to session storage whenever the component unmounts
 * or the user navigates away from the page.
 */
export const UniversalCalculatorClientWrapper = ({
  locale,
  vehicleId,
  vehicle,
  strings,
  financeMessages,
  tenant,
}: Props) => {
  usePersistentCalculator(vehicleId);

  const saveCalculatorState = useSetAtom(saveCurrentCalculatorStateAtom);

  useEffect(() => {
    saveCalculatorState();

    return () => {
      saveCalculatorState();
    };
  }, []);

  return (
    <UniversalCalculator
      locale={locale}
      strings={strings}
      vehicleId={vehicleId}
      vehicle={vehicle}
      financeMessages={financeMessages}
      tenant={tenant}
    />
  );
};
